import React from 'react';
import { ArrowRight } from 'lucide-react';

const smallStats = [
  { v: '312+', l: 'Qualified meetings booked', s: 'Last 12 months' },
  { v: '20+', l: 'Active pipelines running', s: 'Across 14 B2B industries' },
  { v: '97%', l: 'Client retention rate', s: 'Month-over-month' },
];

const feed = [
  ['New qualified meeting booked, FinOps SaaS', '2m ago'],
  ['Positive reply, marketing agency, CA', '14m ago'],
  ['Campaign optimized, +2.1pt reply rate', '41m ago'],
  ['New qualified meeting booked, consulting firm', '1h ago'],
  ['New domain warmed to 94% inbox placement', '3h ago'],
];

const cases = [
  { initials: 'IA', logo: '/logos/grovrtesting.png',   name: 'GroVR', meta: 'Product Testing SaaS', window: '30 days', title: '21 qualified calls, zero hands-on time', rows: [['21','Qualified calls'],['$84K','Pipeline opened']], foot: '+11.2pt reply rate' },
  { initials: 'AG', logo: '/logos/icedautomations.png', name: 'Iced Automations', meta: 'Automation Agency', window: '8 weeks', title: '+$27K net-new revenue, fully hands-off', rows: [['$27K','Net-new revenue'],['18 days','To first close']], foot: '+42% pipeline value' },
  { initials: 'TX', logo: '/logos/tenderx.png',         name: 'TenderX', meta: 'Tender Acquisition Platform', window: 'First month', title: '45 qualified leads, compounding monthly', rows: [['45','Qualified leads'],['+38%','MoM pipeline growth']], foot: '+312K pipeline opened' },
];

const tickerItems = [
  'New qualified meeting, FinOps SaaS, NYC',
  '+42% pipeline growth, B2B consulting client',
  'Domain warmed to 96% inbox placement',
  'Positive reply, marketing agency, CA',
  'Week-one meeting booked for new SaaS client',
  '11.4% avg reply rate across active campaigns',
  'New qualified meeting, PropTech VP, Austin',
  '+$62K ARR closed from Q2 cohort',
];

export default function Receipts() {
  return (
    <section id="case-studies">
      <div className="container-x">
        <div className="section-intro">
          <span className="eyebrow reveal">Receipts</span>
          <h2 className="section-heading reveal reveal-delay-1" style={{ marginTop: 24 }}>
            Numbers that <span className="text-gradient">speak for themselves.</span>
          </h2>
          <p className="reveal reveal-delay-2">Every pipeline we run is measured against one metric: qualified meetings on your calendar. Here's the aggregate scoreboard, and a handful of recent wins.</p>
        </div>

        <div className="receipts-grid">
          <div className="receipt-big reveal">
            <div className="label">opportunities generated worth</div>
            <div className="value"><span className="value-accent">$4.8M+</span></div>
            <div className="sub">Lifetime across 20+ active client pipelines · last 12 months alone</div>
            <div className="receipt-trend">
              <div className="receipt-trend-head">
                <span className="t">Client revenue trend</span>
                <span className="d">+62% YoY</span>
              </div>
              <svg viewBox="0 0 400 80" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="sg" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#1A75FF" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="#1A75FF" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <path d="M0,70 L60,60 L120,55 L180,42 L240,30 L300,20 L360,12 L400,8 L400,80 L0,80 Z" fill="url(#sg)"/>
                <path d="M0,70 L60,60 L120,55 L180,42 L240,30 L300,20 L360,12 L400,8" fill="none" stroke="#1A75FF" strokeWidth="2"/>
              </svg>
              <div className="receipt-trend-x">
                <span>Q1 '24</span><span>Q2 '24</span><span>Q3 '24</span><span>Q4 '24</span><span>Q1 '25</span><span>Q2 '25</span><span>Now</span>
              </div>
            </div>
          </div>

          <div className="receipt-small-grid">
            {smallStats.map((s, i) => (
              <div key={i} className="receipt-small reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="v">{s.v}</div>
                <div className="l">{s.l}</div>
                <div className="s">{s.s}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="live-feed reveal">
          <div className="live-feed-head">
            <h4>Live activity</h4>
            <span className="live-badge">● Live</span>
          </div>
          <ul className="live-feed-list">
            {feed.map((f, i) => (
              <li key={i}><span>{f[0]}</span><span className="ago">{f[1]}</span></li>
            ))}
          </ul>
        </div>

        <div className="case-snapshots">
          <div className="case-head reveal">
            <div>
              <span className="eyebrow">Recent wins</span>
              <h3 style={{ marginTop: 16 }}>Three snapshots from the last 60 days.</h3>
            </div>
            <a href="#case-studies">View all case studies <ArrowRight size={14}/></a>
          </div>
          <div className="case-grid">
            {cases.map((c, i) => (
              <article key={i} className="case-card reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="case-top">
                  <div className="case-avatar">
                    {c.logo ? (
                      <img
                        src={c.logo}
                        alt={`${c.name} logo`}
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentNode.textContent = c.initials; }}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      c.initials
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="case-name">{c.name}</div>
                    <div className="case-meta">{c.meta}</div>
                  </div>
                  <span className="case-window">{c.window}</span>
                </div>
                <h4>{c.title}</h4>
                {c.rows.map((r, j) => (
                  <div key={j} className="case-row">
                    <div className="v">{r[0]}</div>
                    <div className="l">{r[1]}</div>
                  </div>
                ))}
                <div className="case-foot">{c.foot}</div>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div className="ticker container-x">
        <div className="ticker-track">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((t, i) => (
            <div key={i} className="ticker-item">{t}</div>
          ))}
        </div>
      </div>
    </section>
  );
}
